import React, { useState } from "react";
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
  Save as SaveIcon,
  Send as SubmitIcon,
  Visibility as ReviewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDownward as TurnToDraftIcon,
  OpenInNew as OpenCreatedIcon,
} from "@material-ui/icons";
import { formatMessage } from "@openimis/fe-core";

const styles = (theme) => ({
  buttonGroup: {
    margin: theme.spacing(1, 0),
    gap: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  successButton: {
    backgroundColor: "#4caf50",
    color: "white",
    "&:hover": {
      backgroundColor: "#388e3c",
    },
  },
  warningButton: {
    backgroundColor: "#ff9800",
    color: "white",
    "&:hover": {
      backgroundColor: "#f57c00",
    },
  },
  dangerButton: {
    backgroundColor: "#f44336",
    color: "white",
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
  infoButton: {
    backgroundColor: "#2196f3",
    color: "white",
    "&:hover": {
      backgroundColor: "#1976d2",
    },
  },
  dialogContent: {
    minWidth: 400,
  },
});

const EnrollmentActionButtons = ({
  classes,
  intl,
  enrollment,
  currentUser,
  isLoading = false,
  onEdit,
  onSave,
  onSubmit,
  onReview,
  onApprove,
  onReject,
  onTurnToDraft,
  onDelete,
  onOpenCreated,
  openCreatedLabel,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine user permissions
  const isAdmin =
    currentUser?.role === "admin" ||
    currentUser?.permissions?.includes("enrollment_admin");

  // Status checks
  const isDraft = enrollment.status === "DRAFT";
  const isPending = enrollment.status === "PENDING";
  const isInReview = enrollment.status === "IN_REVIEW";
  const isApproved = enrollment.status === "APPROVED";
  const isRejected = enrollment.status === "REJECTED";
  const isExpired = enrollment.status === "EXPIRED";
  const isCancelled = enrollment.status === "CANCELLED";

  // Permission checks based on status workflow
  const canEdit = (enrollment.canEdit !== false) && isDraft;
  const canSubmit = isDraft; // payload completeness validated on backend submit
  const canReview = isPending;
  const canApprove = isInReview;
  const canReject = isPending || isInReview;
  const canTurnToDraft = isPending || isInReview || isRejected;
  const canDelete = isDraft;

  const handleDialogOpen = (type) => {
    setDialogType(type);
    setReason("");
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogType(null);
    setReason("");
  };

  const handleDialogConfirm = async () => {
    setIsSubmitting(true);
    try {
      switch (dialogType) {
        case "approve":
          await onApprove({
            uuid: enrollment.uuid,
            approvalReason: reason,
          });
          break;
        case "reject":
          await onReject({
            uuid: enrollment.uuid,
            rejectionReason: reason,
          });
          break;
        case "turnToDraft":
          await onTurnToDraft({
            enrollmentUuid: enrollment.uuid,
            reason,
          });
          break;
        case "delete":
          await onDelete(enrollment.uuid);
          break;
        case "review":
          await onReview({
            enrollmentUuid: enrollment.uuid,
            note: reason,
          });
          break;
        default:
          break;
      }
      handleDialogClose();
    } catch (error) {
      console.error("Action failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogTitle = () => {
    switch (dialogType) {
      case "approve":
        return formatMessage(intl, "enrollment", "dialog.approve.title");
      case "reject":
        return formatMessage(intl, "enrollment", "dialog.reject.title");
      case "turnToDraft":
        return formatMessage(intl, "enrollment", "dialog.turnToDraft.title");
      case "delete":
        return formatMessage(intl, "enrollment", "dialog.delete.title");
      case "review":
        return "Start Review";
      default:
        return "Confirm Action";
    }
  };

  const getDialogMessage = () => {
    switch (dialogType) {
      case "approve":
        return formatMessage(intl, "enrollment", "dialog.approve.message");
      case "reject":
        return formatMessage(intl, "enrollment", "dialog.reject.message");
      case "turnToDraft":
        return formatMessage(intl, "enrollment", "dialog.turnToDraft.message");
      case "delete":
        return formatMessage(intl, "enrollment", "dialog.delete.message");
      case "review":
        return "Are you sure you want to start reviewing this enrollment?";
      default:
        return "Are you sure you want to perform this action?";
    }
  };

  const getReasonLabel = () => {
    switch (dialogType) {
      case "approve":
        return formatMessage(intl, "enrollment", "dialog.approve.reason");
      case "reject":
        return formatMessage(intl, "enrollment", "dialog.reject.reason");
      case "turnToDraft":
        return formatMessage(intl, "enrollment", "dialog.turnToDraft.reason");
      case "review":
        return "Review Notes";
      default:
        return "Reason";
    }
  };

  const isReasonRequired = dialogType === "reject";

  return (
    <Box>
      <div className={classes.buttonGroup}>
        {isApproved && onOpenCreated && (
          <Button
            variant="contained"
            startIcon={<OpenCreatedIcon />}
            onClick={onOpenCreated}
            disabled={isLoading}
            className={classes.infoButton}
          >
            {openCreatedLabel ||
              formatMessage(intl, "enrollment", "action.openCreated") ||
              "Open created record"}
          </Button>
        )}

        {/* Edit/Save Controls */}
        {canEdit && onEdit && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={onEdit}
            disabled={isLoading}
            className={classes.primaryButton}
          >
            {formatMessage(intl, "enrollment", "action.edit")}
          </Button>
        )}

        {canEdit && onSave && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={isLoading}
            className={classes.primaryButton}
          >
            {isLoading
              ? formatMessage(intl, "enrollment", "action.saving")
              : formatMessage(intl, "enrollment", "action.save")}
          </Button>
        )}

        {/* Submit for Review */}
        {canSubmit && onSubmit && (
          <Button
            variant="contained"
            startIcon={<SubmitIcon />}
            onClick={() => onSubmit({ enrollmentUuid: enrollment.uuid })}
            disabled={isLoading}
            className={classes.successButton}
          >
            {formatMessage(intl, "enrollment", "action.submitForReview")}
          </Button>
        )}

        {/* Admin Review Actions */}
        {canReview && onReview && (
          <Button
            variant="contained"
            startIcon={<ReviewIcon />}
            onClick={() => handleDialogOpen("review")}
            disabled={isLoading}
            className={classes.infoButton}
          >
            {formatMessage(intl, "enrollment", "action.review")}
          </Button>
        )}

        {/* Admin Approval Actions */}
        {canApprove && onApprove && (
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => handleDialogOpen("approve")}
            disabled={isLoading}
            className={classes.successButton}
          >
            {formatMessage(intl, "enrollment", "action.approve")}
          </Button>
        )}

        {canReject && onReject && (
          <Button
            variant="contained"
            startIcon={<RejectIcon />}
            onClick={() => handleDialogOpen("reject")}
            disabled={isLoading}
            className={classes.dangerButton}
          >
            {formatMessage(intl, "enrollment", "action.reject")}
          </Button>
        )}

        {/* Turn to Draft */}
        {canTurnToDraft && onTurnToDraft && (
          <Button
            variant="contained"
            startIcon={<TurnToDraftIcon />}
            onClick={() => handleDialogOpen("turnToDraft")}
            disabled={isLoading}
            className={classes.warningButton}
          >
            {formatMessage(intl, "enrollment", "action.turnToDraft")}
          </Button>
        )}

        {/* Delete */}
        {canDelete && onDelete && (
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => handleDialogOpen("delete")}
            disabled={isLoading}
            className={classes.dangerButton}
          >
            {formatMessage(intl, "enrollment", "action.delete")}
          </Button>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body1" gutterBottom>
            {getDialogMessage()}
          </Typography>

          {(dialogType === "approve" ||
            dialogType === "reject" ||
            dialogType === "turnToDraft" ||
            dialogType === "review") && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label={getReasonLabel()}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              margin="normal"
              required={isReasonRequired}
              error={isReasonRequired && !reason.trim()}
              helperText={
                isReasonRequired && !reason.trim()
                  ? "This field is required"
                  : ""
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            disabled={isSubmitting || (isReasonRequired && !reason.trim())}
            variant="contained"
            color="primary"
            startIcon={isSubmitting && <CircularProgress size={16} />}
          >
            {isSubmitting ? "Processing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withStyles(styles)(EnrollmentActionButtons);
